import type { ApiResponse } from "../../shared/types";
import { isAuthConfigured } from "../auth/authConfig";
import { reportResponseCache } from "./reportCache";

function resolveApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  if (import.meta.env.PROD || window.location.hostname.endsWith(".azurestaticapps.net")) {
    return "https://gen7datahealth.azurewebsites.net/api";
  }

  return "/api";
}

const apiBaseUrl = resolveApiBaseUrl();
type AccessTokenProvider = () => Promise<string | undefined>;

let accessTokenProvider: AccessTokenProvider | undefined;
const lastSuccessfulGet = new Map<string, unknown>();

export class ApiRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly retryAfterSeconds?: number) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export interface ApiStatus {
  apiAvailable: boolean;
  dataMode: "mock" | "sql" | "unknown";
  message?: string;
}

const listeners = new Set<(status: ApiStatus) => void>();

function notifyApiStatus(status: ApiStatus) {
  listeners.forEach((listener) => listener(status));
}

function isAuthenticationError(error: unknown): boolean {
  return error instanceof Error && error.message.includes("Authentication is required");
}

async function readApiErrorMessage(response: Response): Promise<string | undefined> {
  try {
    const payload = await response.json() as Partial<ApiResponse<unknown>>;
    return payload.error?.message || payload.message;
  } catch {
    return undefined;
  }
}

export function subscribeApiStatus(listener: (status: ApiStatus) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setAccessTokenProvider(provider: AccessTokenProvider | undefined) {
  reportResponseCache.clear();
  accessTokenProvider = provider;
}

async function request<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  if (isAuthConfigured && !accessTokenProvider) {
    throw new Error("Authentication is required to access the dashboard API. No API token provider is ready.");
  }

  const token = await accessTokenProvider?.();
  if (accessTokenProvider && !token) {
    throw new Error("Authentication is required to access the dashboard API. No API access token was returned.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers
    },
    ...options
  });

  if (!response.ok) {
    const detail = await readApiErrorMessage(response);
    if (response.status === 401 || response.status === 403) {
      throw new Error(detail || "Authentication is required to access the dashboard API.");
    }

    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new ApiRequestError(
      detail ? `API returned ${response.status}: ${detail}` : `API returned ${response.status}`,
      response.status,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined
    );
  }

  return response.json() as Promise<ApiResponse<T>>;
}

export async function getWithFallback<T>(path: string, fallback: () => T): Promise<T> {
  try {
    const response = await request<T>(path);
    if (!response.success || response.data === undefined) {
      throw new Error(response.error?.message || "API response did not include data");
    }
    notifyApiStatus({
      apiAvailable: true,
      dataMode: response.meta?.dataMode || "unknown",
      message: response.message
    });
    lastSuccessfulGet.set(path, response.data);
    return response.data;
  } catch (error) {
    if (isAuthenticationError(error)) {
      notifyApiStatus({
        apiAvailable: false,
        dataMode: "unknown",
        message: error instanceof Error ? error.message : "Authentication is required."
      });
      throw error;
    }

    if (error instanceof ApiRequestError && error.status === 429) {
      notifyApiStatus({
        apiAvailable: true,
        dataMode: "sql",
        message: "The latest saved report is shown while the data source refreshes."
      });
      return (lastSuccessfulGet.get(path) as T | undefined) ?? fallback();
    }

    notifyApiStatus({
      apiAvailable: false,
      dataMode: "mock",
      message: error instanceof Error ? error.message : "API unavailable; local mock fallback is active."
    });
    return fallback();
  }
}

export async function postWithFallback<TPayload, TResult>(
  path: string,
  payload: TPayload,
  fallback: (payload: TPayload) => TResult
): Promise<TResult> {
  try {
    const response = await request<TResult>(path, {
      method: "POST",
      body: JSON.stringify(payload)
    });
    if (!response.success || response.data === undefined) {
      throw new Error(response.error?.message || "API response did not include data");
    }
    notifyApiStatus({
      apiAvailable: true,
      dataMode: response.meta?.dataMode || "unknown",
      message: response.message
    });
    return response.data;
  } catch (error) {
    if (isAuthenticationError(error)) {
      notifyApiStatus({
        apiAvailable: false,
        dataMode: "unknown",
        message: error instanceof Error ? error.message : "Authentication is required."
      });
      throw error;
    }

    notifyApiStatus({
      apiAvailable: false,
      dataMode: "mock",
      message: error instanceof Error ? error.message : "API unavailable; local mock fallback is active."
    });
    return fallback(payload);
  }
}

export async function getRequired<T>(path: string): Promise<T> {
  const response = await request<T>(path);
  if (!response.success || response.data === undefined) {
    throw new Error(response.error?.message || "API response did not include data");
  }
  notifyApiStatus({
    apiAvailable: true,
    dataMode: response.meta?.dataMode || "unknown",
    message: response.message
  });
  return response.data;
}

export async function postRequired<TPayload, TResult>(path: string, payload: TPayload): Promise<TResult> {
  const response = await request<TResult>(path, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  if (!response.success || response.data === undefined) {
    throw new Error(response.error?.message || "API response did not include data");
  }
  notifyApiStatus({
    apiAvailable: true,
    dataMode: response.meta?.dataMode || "unknown",
    message: response.message
  });
  return response.data;
}

export async function patchRequired<TPayload, TResult>(path: string, payload: TPayload): Promise<TResult> {
  const response = await request<TResult>(path, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
  if (!response.success || response.data === undefined) {
    throw new Error(response.error?.message || "API response did not include data");
  }
  notifyApiStatus({
    apiAvailable: true,
    dataMode: response.meta?.dataMode || "unknown",
    message: response.message
  });
  return response.data;
}

export async function deleteRequired<TResult>(path: string): Promise<TResult> {
  const response = await request<TResult>(path, {
    method: "DELETE"
  });
  if (!response.success || response.data === undefined) {
    throw new Error(response.error?.message || "API response did not include data");
  }
  notifyApiStatus({
    apiAvailable: true,
    dataMode: response.meta?.dataMode || "unknown",
    message: response.message
  });
  return response.data;
}
